import React, { useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getRequestedTeamId } from "../utils/teamRouteTarget";
import { useTeam } from "../context/TeamContext";
import { useTeamDashboardAccess } from "../components/access/RouteAccess";
import PageExperience from "../components/page-experience/PageExperience";
import { createPageIdentity } from "../components/page-experience/pageExperienceModel";
import "./TeamDashboardPage.css";

const loadLive = () => import("../components/core-components/Team-Overview");
const loadExample = () => import("../components/team-dashboard/teamDashboardExampleAdapter");

export default function TeamDashboardPage({ Experience = PageExperience }) {
  const location = useLocation(), navigate = useNavigate();
  const { teamId: activeTeamId } = useTeam();
  const state = useTeamDashboardAccess();
  const requestedTeamId = getRequestedTeamId(location.search);
  const setupRequested = new URLSearchParams(location.search).get("openCreateTeam") === "1";
  const setupOnly = Boolean(state.userData && !state.accountLoading && !state.accountError &&
    setupRequested && !state.isActiveTeamMember && !state.pendingJoinAccess?.active);
  // Consume a fulfilled manager shortcut, preserving open-create commands.
  useLayoutEffect(() => {
    if (!requestedTeamId || requestedTeamId !== activeTeamId) return;
    const params = new URLSearchParams(location.search);
    params.delete("teamId");
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : "", hash: location.hash }, { replace: true });
  }, [requestedTeamId, activeTeamId, location.pathname, location.search, location.hash, navigate]);

  // A signed-in account with no team gets real onboarding, never fictional data.
  const permitted = state.userData && (!state.resolvedTeamId || state.isActiveTeamMember || state.pendingJoinAccess?.active);
  // Creating a team is an account action, including when an old team is inaccessible.
  // Do not mount that team's controller merely to open the setup form.
  const status = setupOnly ? "permitted" : state.error ? "error" : state.isLoading ? "pending" : permitted ? "permitted" : "denied";
  return <main className="TeamDashboardPage" aria-label="Team Settings">
    <Experience identity={createPageIdentity({ accountId: state.userData?.uid,
      teamId: setupOnly ? null : state.resolvedTeamId, routeId: "team-dashboard" })}
      access={{ status, reason: state.userData ? "no-membership" : "signed-out", retry: state.refreshAccess }}
      loadLive={loadLive} loadExample={loadExample}
      returnTo={location.pathname + location.search + (location.hash || "")}
      onAccessAction={({ id, returnTo }) => navigate(id === "sign-in"
        ? "/login?next=" + encodeURIComponent(returnTo) : "/team-center/settings?openCreateTeam=1")} />
  </main>;
}
